import { DIALOGS } from "@/types";
import { dialogEventEmitter, DIALOG_EVENTS } from "@/events";
import type {
    LockFormDialogProps,
    LockTransactionProps,
    MintTransactionProps,
    DepositTransactionProps,
    RedeemTransactionProps,
    RedeemFormDialogProps,
    WithdrawTransactionProps,
    LockMoreFormDialogProps,
    ChangeDurationFormDialogProps,
    WithdrawLockFormDialogProps,
    ChangeDurationTransactionProps,
    CancelExitTransactionProps,
    ClaimTransactionProps,
    WithdrawUnwoundTransactionProps,
    RedeemFormDialogAltProps,
    JoinWaitlistDialogProps,
    VoteSubmitDialogProps,
    VoteSuccessToastProps,
    WaitlistInterceptorDialogProps,
} from "@/components";
import type { DIALOG_COMPONENTS } from "@/hoc";
import type { OpenDialogProps } from "./use-dialog-portal.types";
import type { ReallocatePointsDialogProps } from "@/components/dialogs/PointsReallocateDialog";

export function useDialogPortal() {
    const closeDialog = () => {
        dialogEventEmitter.emit(DIALOG_EVENTS.CLOSE, {});
    };

    const openDialog = <T extends keyof typeof DIALOG_COMPONENTS>({ dialogType, dialogProps }: OpenDialogProps<T>) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType,
            props: dialogProps,
        });
    };

    const openLockFormDialog = (props: LockFormDialogProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.LOCK_FORM,
            props,
        });
    };

    const openLockTransactionDialog = (props: LockTransactionProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.LOCK_CONFIRM,
            props,
        });
    };

    const openMintTransactionDialog = (props: MintTransactionProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.MINT_CONFIRM,
            props,
        });
    };

    const openDepositTransactionDialog = (props: DepositTransactionProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.DEPOSIT_CONFIRM,
            props,
        });
    };

    const openRedeemTransactionDialog = (props: RedeemTransactionProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.REDEEM_CONFIRM,
            props,
        });
    };

    const openRedeemFormDialog = (props: RedeemFormDialogProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.REDEEM_FORM,
            props,
        });
    };

    const openRedeemFormDialogAlt = (props: RedeemFormDialogAltProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.REDEEM_FORM_ALT,
            props,
        });
    };

    const openWithdrawTransactionDialog = (props: WithdrawTransactionProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.WITHDRAW_CONFIRM,
            props,
        });
    };

    const openLockMoreFormDialog = (props: LockMoreFormDialogProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.LOCK_MORE_FORM,
            props,
        });
    };

    const openChangeDurationFormDialog = (props: ChangeDurationFormDialogProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.CHANGE_DURATION_FORM,
            props,
        });
    };

    const openChangeDurationTransactionDialog = (props: ChangeDurationTransactionProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.CHANGE_DURATION_CONFIRM,
            props,
        });
    };

    const openWithdrawLockFormDialog = (props: WithdrawLockFormDialogProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.WITHDRAW_LOCK_FORM,
            props,
        });
    };

    const openReallocatePointsDialog = (props: ReallocatePointsDialogProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.REALLOCATE_POINTS,
            props,
        });
    };

    const openCancelExitTransactionDialog = (props: CancelExitTransactionProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.CANCEL_EXIT_CONFIRM,
            props,
        });
    };

    const openClaimRedemptionDialog = (props: ClaimTransactionProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.CLAIM_CONFIRM,
            props,
        });
    };

    const openWithdrawUnwoundDialog = (props: WithdrawUnwoundTransactionProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.WITHDRAW_UNWOUND_CONFIRM,
            props,
        });
    };

    const openJoinWaitlistDialog = (props: JoinWaitlistDialogProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.JOIN_WAITLIST,
            props,
        });
    };

    const openWaitlistInterceptorDialog = (props: WaitlistInterceptorDialogProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.WAITLIST_INTERCEPTOR,
            props,
        });
    };

    const openVoteSubmitDialog = (props: VoteSubmitDialogProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.VOTE_SUBMIT,
            props,
        });
    };

    const openVoteSuccessDialog = (props: VoteSuccessToastProps) => {
        dialogEventEmitter.emit(DIALOG_EVENTS.OPEN, {
            dialogType: DIALOGS.VOTE_SUCCESS,
            props,
        });
    };

    return {
        openLockFormDialog,
        openLockTransactionDialog,
        openMintTransactionDialog,
        openLockMoreFormDialog,
        openDepositTransactionDialog,
        openRedeemTransactionDialog,
        openRedeemFormDialog,
        openRedeemFormDialogAlt,
        openWithdrawTransactionDialog,
        openChangeDurationFormDialog,
        openChangeDurationTransactionDialog,
        openWithdrawLockFormDialog,
        openReallocatePointsDialog,
        openCancelExitTransactionDialog,
        openClaimRedemptionDialog,
        openWithdrawUnwoundDialog,
        openJoinWaitlistDialog,
        openWaitlistInterceptorDialog,
        openVoteSubmitDialog,
        openVoteSuccessDialog,
        closeDialog,
        openDialog,
    };
}
